<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-4"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="head-icons ms-2">
          <a
            href="javascript:void(0);"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-original-title="Collapse"
            id="collapse-header"
            @click="toggleHeader"
          >
            <i class="ti ti-chevrons-up"></i>
          </a>
        </div>
      </div>

      <settings-tab />

      <div class="row">
        <general-sidebar />

        <div class="col-xl-9">
          <div class="card border-0 shadow-sm">
            <div class="card-body p-4">
              <div class="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-4 pb-3 border-bottom">
                <div class="d-flex align-items-center">
                  <div class="avatar avatar-lg bg-primary-transparent rounded me-3">
                    <i class="ti ti-building fs-24 text-primary"></i>
                  </div>
                  <div>
                    <h4 class="mb-1 fw-semibold">Company details</h4>
                    <p class="text-muted mb-0 small">
                      Used on payslips, invoices, and statutory reports
                    </p>
                  </div>
                </div>
                <router-link
                  to="/general-settings/security-settings"
                  class="small"
                >
                  Your name, email, and password are in Security
                </router-link>
              </div>

              <form @submit.prevent="submitForm" novalidate>
                <div class="mb-4" id="section-logo">
                  <div class="card bg-light border-0">
                    <div class="card-body p-4">
                      <div class="row align-items-center">
                        <div class="col-auto">
                          <label class="dq-logo-picker mb-0">
                            <input
                              ref="logoInput"
                              type="file"
                              class="d-none"
                              accept="image/*"
                              @change="onLogoChange"
                            />
                            <span class="dq-company-logo">
                              <img
                                v-if="companyForm.imageUrl"
                                :src="companyForm.imageUrl"
                                alt="Company logo"
                              />
                              <span v-else class="dq-company-logo-empty">
                                <i class="ti ti-building"></i>
                              </span>
                            </span>
                            <span class="dq-logo-camera" title="Upload logo">
                              <i class="ti ti-camera text-white fs-16"></i>
                            </span>
                          </label>
                        </div>
                        <div class="col">
                          <h6 class="mb-2 fw-semibold">Company logo</h6>
                          <p class="text-muted small mb-3">
                            Square or landscape logo works best. Maximum size: 4MB.
                            This prints on payslips and invoices.
                          </p>
                          <div class="d-flex align-items-center gap-2">
                            <button
                              type="button"
                              class="btn btn-primary btn-sm"
                              @click="pickLogo"
                            >
                              <i class="ti ti-upload me-1"></i>
                              Upload logo
                            </button>
                            <button
                              v-if="companyForm.imageUrl"
                              type="button"
                              class="btn btn-outline-danger btn-sm"
                              @click="removeLogo"
                            >
                              <i class="ti ti-trash me-1"></i>
                              Remove
                            </button>
                          </div>
                          <div v-if="fieldErrors.logo" class="invalid-feedback d-block">
                            {{ fieldErrors.logo }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mb-4" id="section-company">
                  <div class="d-flex align-items-center mb-3">
                    <div class="avatar avatar-sm bg-success-transparent rounded me-2">
                      <i class="ti ti-building fs-16 text-success"></i>
                    </div>
                    <h5 class="mb-0 fw-semibold">Company information</h5>
                  </div>

                  <div class="card border">
                    <div class="card-body p-4">
                      <div class="row g-3">
                        <div class="col-md-6">
                          <label class="form-label fw-medium">
                            Company name <span class="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': fieldErrors.company_name }"
                            v-model="companyForm.company_name"
                            placeholder="Registered company name"
                          />
                          <div v-if="fieldErrors.company_name" class="invalid-feedback">
                            {{ fieldErrors.company_name }}
                          </div>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">
                            Company email <span class="text-danger">*</span>
                          </label>
                          <input
                            type="email"
                            class="form-control"
                            :class="{ 'is-invalid': fieldErrors.email }"
                            v-model="companyForm.email"
                            placeholder="info@company.com"
                          />
                          <div v-if="fieldErrors.email" class="invalid-feedback">
                            {{ fieldErrors.email }}
                          </div>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">
                            Company PIN <span class="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': fieldErrors.kra_pin }"
                            v-model="companyForm.kra_pin"
                            placeholder="Tax / KRA PIN"
                          />
                          <div v-if="fieldErrors.kra_pin" class="invalid-feedback">
                            {{ fieldErrors.kra_pin }}
                          </div>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">
                            Company phone <span class="text-danger">*</span>
                          </label>
                          <input
                            type="tel"
                            class="form-control"
                            :class="{ 'is-invalid': fieldErrors.phone }"
                            v-model="companyForm.phone"
                            placeholder="+254 700 000000"
                          />
                          <div v-if="fieldErrors.phone" class="invalid-feedback">
                            {{ fieldErrors.phone }}
                          </div>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">Company NSSF</label>
                          <input
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': fieldErrors.nssf }"
                            v-model="companyForm.nssf"
                            placeholder="NSSF number"
                          />
                          <div v-if="fieldErrors.nssf" class="invalid-feedback">
                            {{ fieldErrors.nssf }}
                          </div>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">Company NHIF / SHIF / SHA</label>
                          <input
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': fieldErrors.shif }"
                            v-model="companyForm.shif"
                            placeholder="NHIF / SHIF / SHA number"
                          />
                          <div v-if="fieldErrors.shif" class="invalid-feedback">
                            {{ fieldErrors.shif }}
                          </div>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">Phone 2</label>
                          <input
                            type="tel"
                            class="form-control"
                            v-model="companyForm.phone_two"
                            placeholder="Secondary contact (optional)"
                          />
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">Website</label>
                          <input
                            type="text"
                            class="form-control"
                            v-model="companyForm.website_url"
                            placeholder="https://www.company.com"
                          />
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">
                            Currency <span class="text-danger">*</span>
                          </label>
                          <vue-select
                            :options="currencyOptions"
                            placeholder="Select currency"
                            v-model="companyForm.currency"
                            :class="{ 'is-invalid': fieldErrors.currency }"
                          />
                          <div v-if="fieldErrors.currency" class="invalid-feedback d-block">
                            {{ fieldErrors.currency }}
                          </div>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">
                            Timezone <span class="text-danger">*</span>
                          </label>
                          <vue-select
                            :options="timezones"
                            placeholder="Select timezone"
                            v-model="companyForm.timezone"
                            :clearable="false"
                            :class="{ 'is-invalid': fieldErrors.timezone }"
                          />
                          <div v-if="fieldErrors.timezone" class="invalid-feedback d-block">
                            {{ fieldErrors.timezone }}
                          </div>
                        </div>

                        <div class="col-md-12">
                          <label class="form-label fw-medium">About company</label>
                          <textarea
                            class="form-control"
                            v-model="companyForm.about"
                            placeholder="Brief description of your company"
                            rows="3"
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mb-4" id="section-address">
                  <div class="d-flex align-items-center mb-3">
                    <div class="avatar avatar-sm bg-warning-transparent rounded me-2">
                      <i class="ti ti-map-pin fs-16 text-warning"></i>
                    </div>
                    <h5 class="mb-0 fw-semibold">Address</h5>
                  </div>

                  <div class="card border">
                    <div class="card-body p-4">
                      <div class="row g-3">
                        <div class="col-md-12">
                          <label class="form-label fw-medium">
                            Street address <span class="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': fieldErrors.address }"
                            v-model="companyForm.address"
                            placeholder="Head office street address"
                          />
                          <div v-if="fieldErrors.address" class="invalid-feedback">
                            {{ fieldErrors.address }}
                          </div>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">State / county / region</label>
                          <input
                            type="text"
                            class="form-control"
                            v-model="companyForm.state"
                            placeholder="State, county or region"
                          />
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">
                            Country <span class="text-danger">*</span>
                          </label>
                          <div class="dropdown w-100">
                            <button
                              class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                              :class="{ 'is-invalid': fieldErrors.country }"
                              type="button"
                              data-bs-toggle="dropdown"
                            >
                              <span class="text-truncate">
                                {{ companyForm.country || "Select country" }}
                              </span>
                              <i class="ti ti-chevron-down ms-2"></i>
                            </button>
                            <div class="dropdown-menu p-2 w-100 shadow">
                              <input
                                v-model="countryQuery"
                                type="text"
                                class="form-control mb-2"
                                placeholder="Search country..."
                                autocomplete="off"
                              />
                              <div class="search-list" style="max-height: 240px; overflow-y: auto">
                                <button
                                  v-for="c in filteredCountries"
                                  :key="c.country"
                                  type="button"
                                  class="dropdown-item text-truncate"
                                  @click="chooseCountry(c)"
                                >
                                  {{ c.country }}
                                </button>
                                <div
                                  v-if="filteredCountries.length === 0"
                                  class="text-center py-2 small text-muted"
                                >
                                  No country found
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-if="fieldErrors.country" class="invalid-feedback d-block">
                            {{ fieldErrors.country }}
                          </div>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">Postal / ZIP code</label>
                          <input
                            type="text"
                            class="form-control"
                            v-model="companyForm.zip_code"
                            placeholder="Postal / ZIP code"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mb-4" id="section-bank">
                  <div class="d-flex align-items-center mb-3">
                    <div class="avatar avatar-sm bg-warning-transparent rounded me-2">
                      <i class="ti ti-building-bank fs-16 text-warning"></i>
                    </div>
                    <h5 class="mb-0 fw-semibold">Bank details</h5>
                  </div>

                  <div class="card border">
                    <div class="card-body p-4">
                      <div class="row g-3">
                        <div class="col-md-6">
                          <label class="form-label fw-medium">
                            Bank name
                          </label>
                          <div class="dropdown w-100">
                            <button
                              class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                              :class="{ 'is-invalid': fieldErrors.bank_name }"
                              type="button"
                              data-bs-toggle="dropdown"
                            >
                              <span class="text-truncate">
                                {{ companyForm.bank_name || "Select bank" }}
                              </span>
                              <i class="ti ti-chevron-down ms-2"></i>
                            </button>
                            <div class="dropdown-menu p-2 w-100 shadow">
                              <input
                                v-model="bankQuery"
                                type="text"
                                class="form-control mb-2"
                                placeholder="Search bank..."
                                autocomplete="off"
                              />
                              <div
                                class="search-list"
                                style="max-height: 240px; overflow-y: auto"
                              >
                                <button
                                  v-for="b in filteredBanks"
                                  :key="b.bank_name"
                                  type="button"
                                  class="dropdown-item text-truncate"
                                  @click="chooseBank(b)"
                                >
                                  {{ b.bank_name }}
                                </button>
                                <div
                                  v-if="filteredBanks.length === 0"
                                  class="text-center py-2 small text-muted"
                                >
                                  No bank found
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-if="fieldErrors.bank_name" class="invalid-feedback d-block">
                            {{ fieldErrors.bank_name }}
                          </div>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">Bank code</label>
                          <input
                            v-model="companyForm.bank_code"
                            type="text"
                            class="form-control"
                            readonly
                          />
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">
                            Account number
                          </label>
                          <input
                            v-model="companyForm.account_number"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': fieldErrors.account_number }"
                          />
                          <div v-if="fieldErrors.account_number" class="invalid-feedback">
                            {{ fieldErrors.account_number }}
                          </div>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">
                            Account holder name
                          </label>
                          <input
                            v-model="companyForm.account_holder_name"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': fieldErrors.account_holder_name }"
                          />
                          <div v-if="fieldErrors.account_holder_name" class="invalid-feedback">
                            {{ fieldErrors.account_holder_name }}
                          </div>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">
                            Branch
                          </label>
                          <div class="dropdown w-100">
                            <button
                              class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                              :class="{ 'is-invalid': fieldErrors.branch_location }"
                              type="button"
                              :disabled="!companyForm.bank_name"
                              data-bs-toggle="dropdown"
                            >
                              <span class="text-truncate">
                                {{
                                  companyForm.branch_location ||
                                  (companyForm.bank_name
                                    ? "Select branch"
                                    : "Select bank first")
                                }}
                              </span>
                              <i class="ti ti-chevron-down ms-2"></i>
                            </button>
                            <div
                              v-if="companyForm.bank_name"
                              class="dropdown-menu p-2 w-100 shadow"
                            >
                              <input
                                v-model="branchQuery"
                                type="text"
                                class="form-control mb-2"
                                placeholder="Search branch..."
                                autocomplete="off"
                              />
                              <div
                                class="search-list"
                                style="max-height: 240px; overflow-y: auto"
                              >
                                <button
                                  v-for="b in filteredBranches"
                                  :key="b.bank_name + '-' + b.branch_code"
                                  type="button"
                                  class="dropdown-item text-truncate"
                                  @click="chooseBranch(b)"
                                >
                                  {{ b.branch_name }} ({{ b.branch_code }})
                                </button>
                                <div
                                  v-if="filteredBranches.length === 0"
                                  class="text-center py-2 small text-muted"
                                >
                                  No branch found
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-if="fieldErrors.branch_location" class="invalid-feedback d-block">
                            {{ fieldErrors.branch_location }}
                          </div>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">Branch code</label>
                          <input
                            v-model="companyForm.branch_code"
                            type="text"
                            class="form-control"
                            readonly
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mb-4" id="section-signatory">
                  <div class="d-flex align-items-start mb-3">
                    <div class="avatar avatar-sm bg-info-transparent rounded me-2">
                      <i class="ti ti-signature fs-16 text-info"></i>
                    </div>
                    <div>
                      <h5 class="mb-0 fw-semibold">Company authorized signatory</h5>
                      <p class="mb-0 text-muted small mt-1">
                        Used on invoices, official documents, and signed employment contracts.
                      </p>
                    </div>
                  </div>

                  <div class="card border">
                    <div class="card-body p-4">
                      <div class="row g-3">
                        <div class="col-md-6">
                          <label class="form-label fw-medium">Signatory name</label>
                          <input
                            v-model="companyForm.signatory_name"
                            type="text"
                            class="form-control"
                            placeholder="Authorized signatory name"
                          />
                        </div>
                        <div class="col-md-6">
                          <label class="form-label fw-medium">Signatory title</label>
                          <input
                            v-model="companyForm.signatory_title"
                            type="text"
                            class="form-control"
                            placeholder="Finance Manager"
                          />
                        </div>
                        <div class="col-md-12">
                          <label class="form-label fw-medium">Signature image</label>
                          <div class="border rounded p-3 bg-light">
                            <div class="d-flex flex-column flex-md-row align-items-md-center gap-3">
                              <div class="flex-shrink-0">
                                <div
                                  v-if="companyForm.signatorySignatureUrl"
                                  class="bg-white border rounded p-2"
                                >
                                  <img
                                    :src="companyForm.signatorySignatureUrl"
                                    alt="Signatory signature"
                                    style="max-height: 72px; max-width: 220px"
                                  />
                                </div>
                                <div
                                  v-else
                                  class="border rounded bg-white px-4 py-3 text-muted small"
                                >
                                  No signature uploaded
                                </div>
                              </div>
                              <div class="flex-grow-1">
                                <input
                                  type="file"
                                  class="form-control"
                                  accept="image/*"
                                  @change="onSignatorySignatureChange"
                                />
                                <div class="form-text">
                                  Shown on invoice previews, exported documents, and signed contracts.
                                </div>
                              </div>
                              <div
                                v-if="companyForm.signatorySignatureUrl"
                                class="flex-shrink-0"
                              >
                                <button
                                  type="button"
                                  class="btn btn-light"
                                  @click="removeSignatorySignature"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          </div>
                          <div class="mt-3">
                            <label class="form-label fw-medium mb-1">Or draw the signature</label>
                            <canvas
                              ref="signatoryPad"
                              class="signatory-pad"
                              width="520"
                              height="160"
                              @pointerdown="startSignatoryDraw"
                              @pointermove="moveSignatoryDraw"
                              @pointerup="stopSignatoryDraw"
                              @pointerleave="stopSignatoryDraw"
                            ></canvas>
                            <div class="d-flex flex-wrap gap-2 mt-2">
                              <button type="button" class="btn btn-light" @click="clearSignatoryPad">
                                Clear drawing
                              </button>
                              <button type="button" class="btn btn-outline-primary" @click="useSignatoryDrawing">
                                Use drawing
                              </button>
                            </div>
                            <div class="form-text">
                              Use the drawing, then save the company profile.
                            </div>
                          </div>
                        </div>

                        <div class="col-md-12">
                          <div class="dq-signatory-preview card border mb-0">
                            <div class="card-body">
                              <div class="d-flex align-items-center justify-content-between mb-3">
                                <div>
                                  <h6 class="mb-1 fw-semibold">Preview</h6>
                                  <p class="mb-0 text-muted small">
                                    Sample layout for invoices; the same signatory appears on signed contracts.
                                  </p>
                                </div>
                                <span class="badge bg-light text-dark border">Live preview</span>
                              </div>

                              <div class="dq-signatory-preview-sheet">
                                <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                                  <div class="d-flex align-items-center gap-3">
                                    <template v-if="companyForm.imageUrl">
                                      <img
                                        :src="companyForm.imageUrl"
                                        alt="Company logo"
                                        class="dq-signatory-logo"
                                      />
                                    </template>
                                    <template v-else>
                                      <div class="dq-signatory-logo-fallback">
                                        {{ companyInitials }}
                                      </div>
                                    </template>
                                    <div>
                                      <div class="fw-semibold">
                                        {{ companyForm.company_name || "Company name" }}
                                      </div>
                                      <div class="text-muted small">
                                        {{ companyForm.address || "Company address" }}
                                      </div>
                                    </div>
                                  </div>
                                  <div class="text-end dq-signatory-signblock">
                                    <template v-if="companyForm.signatorySignatureUrl">
                                      <img
                                        :src="companyForm.signatorySignatureUrl"
                                        alt="Signature preview"
                                        class="dq-signatory-image"
                                      />
                                    </template>
                                    <div v-else class="dq-signatory-line"></div>
                                    <div class="fw-semibold mt-2">
                                      {{ companyForm.signatory_name || "Authorized signatory" }}
                                    </div>
                                    <div class="text-muted small">
                                      {{ companyForm.signatory_title || "Finance Manager" }}
                                    </div>
                                  </div>
                                </div>

                                <div class="dq-signatory-footer mt-3 pt-3">
                                  <div class="small text-muted mb-2">
                                    Payment may be made via bank transfer or cheque in the name of
                                    <span class="text-dark fw-medium">
                                      {{
                                        companyForm.account_holder_name ||
                                        companyForm.company_name ||
                                        "Company name"
                                      }}
                                    </span>.
                                  </div>
                                  <div class="d-flex flex-wrap gap-3 small">
                                    <span>
                                      Bank:
                                      <strong>{{ companyForm.bank_name || "Bank name" }}</strong>
                                    </span>
                                    <span>
                                      Account:
                                      <strong>{{ companyForm.account_number || "—" }}</strong>
                                    </span>
                                    <span v-if="companyForm.branch_location">
                                      Branch:
                                      <strong>{{ companyForm.branch_location }}</strong>
                                    </span>
                                    <span v-if="companyForm.branch_code">
                                      Branch code:
                                      <strong>{{ companyForm.branch_code }}</strong>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mb-4" id="section-social">
                  <button
                    class="btn btn-link text-decoration-none px-0"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#socialLinks"
                    aria-expanded="false"
                    aria-controls="socialLinks"
                  >
                    Optional social links
                    <i class="ti ti-chevron-down ms-1"></i>
                  </button>
                  <div id="socialLinks" class="collapse">
                    <div class="card border mt-2">
                      <div class="card-body p-4">
                        <div class="row g-3">
                          <div class="col-md-6">
                            <label class="form-label fw-medium">Facebook</label>
                            <input
                              type="text"
                              class="form-control"
                              v-model="companyForm.facebook_profile"
                              placeholder="Facebook page URL"
                            />
                          </div>
                          <div class="col-md-6">
                            <label class="form-label fw-medium">Twitter / X</label>
                            <input
                              type="text"
                              class="form-control"
                              v-model="companyForm.twitter_profile"
                              placeholder="Twitter / X URL"
                            />
                          </div>
                          <div class="col-md-6">
                            <label class="form-label fw-medium">LinkedIn</label>
                            <input
                              type="text"
                              class="form-control"
                              v-model="companyForm.linkedin_profile"
                              placeholder="LinkedIn company URL"
                            />
                          </div>
                          <div class="col-md-6">
                            <label class="form-label fw-medium">Instagram</label>
                            <input
                              type="text"
                              class="form-control"
                              v-model="companyForm.instagram_profile"
                              placeholder="Instagram URL"
                            />
                          </div>
                          <div class="col-md-6">
                            <label class="form-label fw-medium">WhatsApp</label>
                            <input
                              type="text"
                              class="form-control"
                              v-model="companyForm.whatsapp_profile"
                              placeholder="WhatsApp business number"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="dq-save-bar d-flex align-items-center justify-content-end gap-2">
                  <button
                    type="button"
                    class="btn btn-light px-4"
                    @click="resetAll"
                    :disabled="saving"
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    class="btn btn-primary px-4"
                    :disabled="saving"
                  >
                    <span
                      v-if="saving"
                      class="spinner-border spinner-border-sm me-2"
                    ></span>
                    <i v-else class="ti ti-check me-1"></i>
                    Save changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch, onMounted } from "vue";
import { useAuthStore } from "@/stores/authStore";
import { useCompanyStore } from "@/stores/companyStore";
import { countries as countryList } from "@/utils/countryData";
import { toast } from "@/utils/toast";
import { banks as bankList } from "@/utils/banksData";
import {
  TIMEZONE_OPTIONS,
  COUNTRY_DEFAULT_TIMEZONE,
  normalizeTimezoneValue,
} from "@/utils/timezoneOptions";

const title = "Company details";
const text = "Administration";
const text1 = "Settings";

const authStore = useAuthStore();
const companyStore = useCompanyStore();

const saving = ref(false);
const logoInput = ref(null);
const signatoryPad = ref(null);
const signatoryDrawing = ref(false);
const fieldErrors = reactive({});
const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

const currencyOptions = [
  "Kenyan Shilling (KES)",
  "Ugandan Shilling (UGX)",
  "Tanzanian Shilling (TZS)",
  "Rwandan Franc (RWF)",
  "US Dollar (USD)",
  "Euro (EUR)",
  "British Pound (GBP)",
  "Japanese Yen (JPY)",
  "Chinese Yuan (CNY)",
  "Indian Rupee (INR)",
  "South African Rand (ZAR)",
  "Canadian Dollar (CAD)",
];

const timezones = TIMEZONE_OPTIONS;

const currencyCodeMap = {
  "Kenyan Shilling (KES)": "KES",
  "Ugandan Shilling (UGX)": "UGX",
  "Tanzanian Shilling (TZS)": "TZS",
  "Rwandan Franc (RWF)": "RWF",
  "US Dollar (USD)": "USD",
  "Euro (EUR)": "EUR",
  "British Pound (GBP)": "GBP",
  "Japanese Yen (JPY)": "JPY",
  "Chinese Yuan (CNY)": "CNY",
  "Indian Rupee (INR)": "INR",
  "South African Rand (ZAR)": "ZAR",
  "Canadian Dollar (CAD)": "CAD",
};

const countryCodeMap = {
  Kenya: "KE",
  Uganda: "UG",
  Tanzania: "TZ",
  Rwanda: "RW",
};
const countryTimezoneMap = COUNTRY_DEFAULT_TIMEZONE;

function defaultCompanyForm() {
  return {
    logo: null,
    imageUrl: "",
    company_name: "",
    email: "",
    kra_pin: "",
    nssf: "",
    shif: "",
    phone: "",
    phone_two: "",
    fax: "",
    status: "Active",
    currency: "",
    currency_code: "",
    timezone: "",
    website_url: "",
    about: "",
    address: "",
    state: "",
    country: "",
    country_code: "",
    zip_code: "",
    facebook_profile: "",
    twitter_profile: "",
    linkedin_profile: "",
    skype_profile: "",
    whatsapp_profile: "",
    instagram_profile: "",
    bank_name: "",
    bank_code: "",
    account_number: "",
    account_holder_name: "",
    branch_location: "",
    branch_code: "",
    signatory_name: "",
    signatory_title: "",
    signatory_signature: null,
    signatorySignatureUrl: "",
    remove_signatory_signature: false,
  };
}
const companyForm = reactive(defaultCompanyForm());

const activeCompanyId = computed(() => companyStore.activeCompanyId);
const activeCompany = computed(() => companyStore.company);
const currentUser = computed(() => authStore.currentUser);

const companyInitials = computed(() => {
  return (companyForm.company_name || "CN")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
});

function populateCompanyForm(c) {
  if (!c) return;

  companyForm.company_name = c.company_name || "";
  companyForm.email = c.email || "";
  companyForm.kra_pin = c.kra_pin || "";
  companyForm.nssf = c.nssf || "";
  companyForm.shif = c.shif || "";
  companyForm.phone = c.phone || "";
  companyForm.phone_two = c.phone_two || "";
  companyForm.fax = c.fax || "";
  companyForm.status = c.status || "Active";
  companyForm.currency =
    c.currency && c.currency !== "Select" ? c.currency : "";
  companyForm.currency_code = c.currency_code || "";
  companyForm.timezone = normalizeTimezoneValue(c.timezone);
  companyForm.website_url = c.website_url || "";
  companyForm.about = c.about || "";
  companyForm.address = c.address || "";
  companyForm.state = c.state || "";
  companyForm.country = c.country || "";
  companyForm.country_code = c.country_code || "";
  companyForm.zip_code = c.zip_code || "";
  companyForm.facebook_profile = c.facebook_profile || "";
  companyForm.twitter_profile = c.twitter_profile || "";
  companyForm.linkedin_profile = c.linkedin_profile || "";
  companyForm.skype_profile = c.skype_profile || "";
  companyForm.whatsapp_profile = c.whatsapp_profile || "";
  companyForm.instagram_profile = c.instagram_profile || "";
  companyForm.imageUrl = c.logo_url || "";
  companyForm.logo = null;
  companyForm.bank_name = c.bank_name || "";
  companyForm.bank_code = c.bank_code || "";
  companyForm.account_number = c.account_number || "";
  companyForm.account_holder_name = c.account_holder_name || "";
  companyForm.branch_location = c.branch_location || "";
  companyForm.branch_code = c.branch_code || "";
  companyForm.signatory_name = c.signatory_name || "";
  companyForm.signatory_title = c.signatory_title || "";
  companyForm.signatorySignatureUrl = c.signatory_signature_url || "";
  companyForm.signatory_signature = null;
  companyForm.remove_signatory_signature = false;
}

watch(
  () => activeCompany.value,
  (c) => {
    if (c) populateCompanyForm(c);
  },
  { deep: true }
);

watch(
  () => companyForm.currency,
  (val) => {
    if (!val || val === "Select") {
      companyForm.currency_code = "";
      return;
    }
    const code = currencyCodeMap[val];
    if (code) companyForm.currency_code = code;
  }
);

watch(
  () => companyForm.country,
  (val) => {
    if (!val) {
      companyForm.country_code = "";
      return;
    }
    const code = countryCodeMap[val];
    if (code) {
      companyForm.country_code = code;
      if (!companyForm.timezone) {
        const tz = countryTimezoneMap[code];
        if (tz) companyForm.timezone = tz;
      }
    }
  }
);

const countryQuery = ref("");
const filteredCountries = computed(() => {
  const q = countryQuery.value.trim().toLowerCase();
  if (!q) return countryList;
  return countryList.filter((c) => c.country.toLowerCase().includes(q));
});

function chooseCountry(c) {
  companyForm.country = c.country;
  countryQuery.value = "";
  delete fieldErrors.country;
  const code = countryCodeMap[c.country];
  if (code) {
    companyForm.country_code = code;
    if (!companyForm.timezone) {
      const tz = countryTimezoneMap[code];
      if (tz) companyForm.timezone = tz;
    }
  }
}

const bankQuery = ref("");
const branchQuery = ref("");

const uniqueBanks = computed(() => {
  const map = new Map();
  bankList.forEach((b) => {
    if (!map.has(b.bank_name)) {
      map.set(b.bank_name, b.bank_code);
    }
  });
  return Array.from(map, ([bank_name, bank_code]) => ({
    bank_name,
    bank_code,
  }));
});

const filteredBanks = computed(() => {
  const q = bankQuery.value.trim().toLowerCase();
  if (!q) return uniqueBanks.value;
  return uniqueBanks.value.filter((b) => b.bank_name.toLowerCase().includes(q));
});

const availableBranches = computed(() => {
  if (!companyForm.bank_name) return [];
  return bankList.filter((b) => b.bank_name === companyForm.bank_name);
});

const filteredBranches = computed(() => {
  const q = branchQuery.value.trim().toLowerCase();
  let list = availableBranches.value;
  if (!q) return list;
  return list.filter(
    (b) =>
      b.branch_name.toLowerCase().includes(q) ||
      String(b.branch_code).toLowerCase().includes(q)
  );
});

function chooseBank(bank) {
  companyForm.bank_name = bank.bank_name;
  companyForm.bank_code = bank.bank_code || "";
  companyForm.branch_location = "";
  companyForm.branch_code = "";
  branchQuery.value = "";
  delete fieldErrors.bank_name;
  delete fieldErrors.branch_location;
}

function chooseBranch(branch) {
  companyForm.branch_location = branch.branch_name;
  companyForm.branch_code = branch.branch_code || "";
  delete fieldErrors.branch_location;
}

function pickLogo() {
  logoInput.value?.click();
}

function isValidImage(file) {
  if (!file?.type?.startsWith("image/")) {
    toast("Please select a valid image file", "error");
    return false;
  }
  if (file.size > MAX_IMAGE_BYTES) {
    toast("Image must be 4MB or smaller", "error");
    return false;
  }
  return true;
}

function onLogoChange(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  if (!isValidImage(file)) {
    if (logoInput.value) logoInput.value.value = "";
    return;
  }
  companyForm.logo = file;
  delete fieldErrors.logo;
  const reader = new FileReader();
  reader.onload = (evt) => (companyForm.imageUrl = evt.target.result);
  reader.readAsDataURL(file);
}

function removeLogo() {
  companyForm.logo = null;
  companyForm.imageUrl = "";
  if (logoInput.value) logoInput.value.value = "";
}

function onSignatorySignatureChange(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  if (!isValidImage(file)) {
    e.target.value = "";
    return;
  }
  companyForm.signatory_signature = file;
  companyForm.remove_signatory_signature = false;
  const reader = new FileReader();
  reader.onload = (evt) => (companyForm.signatorySignatureUrl = evt.target.result);
  reader.readAsDataURL(file);
}

function removeSignatorySignature() {
  companyForm.signatory_signature = null;
  companyForm.signatorySignatureUrl = "";
  companyForm.remove_signatory_signature = true;
  clearSignatoryPad();
}

function prepareSignatoryPad() {
  const canvas = signatoryPad.value;
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.strokeStyle = "#111827";
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
}

function signatoryPoint(event) {
  const canvas = signatoryPad.value;
  const rect = canvas.getBoundingClientRect();
  return {
    x: ((event.clientX - rect.left) / rect.width) * canvas.width,
    y: ((event.clientY - rect.top) / rect.height) * canvas.height,
  };
}

function startSignatoryDraw(event) {
  const canvas = signatoryPad.value;
  if (!canvas) return;
  signatoryDrawing.value = true;
  canvas.setPointerCapture?.(event.pointerId);
  const ctx = canvas.getContext("2d");
  const { x, y } = signatoryPoint(event);
  ctx.beginPath();
  ctx.moveTo(x, y);
}

function moveSignatoryDraw(event) {
  if (!signatoryDrawing.value) return;
  const canvas = signatoryPad.value;
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  const { x, y } = signatoryPoint(event);
  ctx.lineTo(x, y);
  ctx.stroke();
}

function stopSignatoryDraw() {
  signatoryDrawing.value = false;
}

function clearSignatoryPad() {
  prepareSignatoryPad();
}

function signatoryPadHasInk() {
  const canvas = signatoryPad.value;
  if (!canvas) return false;
  const data = canvas.getContext("2d").getImageData(0, 0, canvas.width, canvas.height).data;
  for (let i = 0; i < data.length; i += 16) {
    if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) return true;
  }
  return false;
}

function useSignatoryDrawing() {
  const canvas = signatoryPad.value;
  if (!canvas || !signatoryPadHasInk()) {
    toast("Draw the signature first.", "error");
    return;
  }
  const previewUrl = canvas.toDataURL("image/png");
  canvas.toBlob((blob) => {
    if (!blob) {
      toast("Could not use that drawing.", "error");
      return;
    }
    companyForm.signatory_signature = new File([blob], "signatory-signature.png", {
      type: "image/png",
    });
    companyForm.signatorySignatureUrl = previewUrl;
    companyForm.remove_signatory_signature = false;
    prepareSignatoryPad();
    toast("Drawing ready. Save the company profile to keep it.");
  }, "image/png");
}

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function resetAll() {
  Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key]);
  Object.assign(companyForm, defaultCompanyForm());
  populateCompanyForm(activeCompany.value);
  if (logoInput.value) logoInput.value.value = "";
  clearSignatoryPad();
}

function clearFieldErrors() {
  Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key]);
}

function validateCompany() {
  clearFieldErrors();
  const required = [
    ["company_name", "Enter the company name."],
    ["email", "Enter the company email."],
    ["kra_pin", "Enter the company PIN."],
    ["phone", "Enter the company phone."],
    ["currency", "Select a currency."],
    ["timezone", "Select a timezone."],
    ["address", "Enter the street address."],
    ["country", "Select a country."],
  ];

  required.forEach(([key, message]) => {
    const val = String(companyForm[key] || "").trim();
    if (!val || val === "Select") fieldErrors[key] = message;
  });

  if (companyForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyForm.email)) {
    fieldErrors.email = "Enter a valid email address.";
  }

  return Object.keys(fieldErrors).length === 0;
}

function scrollToFirstError() {
  const el = document.querySelector(".is-invalid, .invalid-feedback.d-block");
  el?.closest(".col-md-6, .col-md-12, .mb-4")?.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });
}

async function submitForm() {
  if (!currentUser.value?.id) {
    toast("Unable to determine current user.", "error");
    return;
  }
  if (!activeCompanyId.value) {
    toast("No active company selected.", "error");
    return;
  }
  if (!validateCompany()) {
    toast("Please complete the highlighted fields.", "error");
    scrollToFirstError();
    return;
  }

  saving.value = true;
  try {
    const fd = new FormData();

    if (companyForm.logo instanceof File) {
      fd.append("logo", companyForm.logo, companyForm.logo.name);
    }
    if (companyForm.signatory_signature instanceof File) {
      fd.append(
        "signatory_signature",
        companyForm.signatory_signature,
        companyForm.signatory_signature.name
      );
    }
    if (companyForm.remove_signatory_signature) {
      fd.append("remove_signatory_signature", "1");
    }

    const fieldMappings = {
      company_name: "company_name",
      email: "email",
      kra_pin: "kra_pin",
      nssf: "nssf",
      shif: "shif",
      phone: "phone",
      phone_two: "phone_two",
      fax: "fax",
      status: "status",
      currency: "currency",
      currency_code: "currency_code",
      timezone: "timezone",
      website_url: "website_url",
      about: "about",
      address: "address",
      state: "state",
      country: "country",
      country_code: "country_code",
      zip_code: "zip_code",
      facebook_profile: "facebook_profile",
      twitter_profile: "twitter_profile",
      linkedin_profile: "linkedin_profile",
      skype_profile: "skype_profile",
      whatsapp_profile: "whatsapp_profile",
      instagram_profile: "instagram_profile",
      bank_name: "bank_name",
      bank_code: "bank_code",
      account_number: "account_number",
      account_holder_name: "account_holder_name",
      branch_location: "branch_location",
      branch_code: "branch_code",
      signatory_name: "signatory_name",
      signatory_title: "signatory_title",
    };

    Object.entries(fieldMappings).forEach(([formKey, fdKey]) => {
      const val = companyForm[formKey];
      if (val !== null && val !== undefined && val !== "") {
        fd.append(fdKey, val);
      }
    });

    fd.append("_method", "PUT");

    await companyStore.updateCompany(activeCompanyId.value, fd, true);
    await companyStore.fetchCompany(activeCompanyId.value);
    toast("Company details updated successfully");
  } catch (error) {
    console.error("Error updating company details:", error);
    const msg =
      error?.response?.data?.message ||
      "Failed to update company details. Please try again.";
    toast(msg, "error");
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  if (activeCompanyId.value && !activeCompany.value) {
    await companyStore.fetchCompany(activeCompanyId.value);
  } else if (activeCompany.value) {
    populateCompanyForm(activeCompany.value);
  }
  prepareSignatoryPad();
});
</script>

<style scoped>
.dq-logo-picker {
  position: relative;
  display: inline-block;
  width: 112px;
  height: 112px;
  cursor: pointer;
}
.dq-company-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 112px;
  height: 112px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}
.dq-company-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 8px;
}
.dq-company-logo-empty {
  color: #9ca3af;
  font-size: 2rem;
}
.dq-logo-camera {
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--bs-primary);
  border: 3px solid #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.dq-save-bar {
  position: sticky;
  bottom: 0;
  z-index: 5;
  margin-top: 8px;
  padding: 14px 0 4px;
  background: #fff;
  border-top: 1px solid #eef0f3;
}
.dq-signatory-preview-sheet {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
}
.dq-signatory-logo {
  width: 54px;
  height: 54px;
  object-fit: contain;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #fff;
  padding: 6px;
}
.dq-signatory-logo-fallback {
  width: 54px;
  height: 54px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #111827;
  color: #fff;
  font-weight: 700;
}
.dq-signatory-signblock {
  min-width: 220px;
}
.dq-signatory-image {
  max-height: 56px;
  max-width: 220px;
  object-fit: contain;
}
.dq-signatory-line {
  width: 180px;
  height: 48px;
  margin-left: auto;
  border-bottom: 1px solid #cfd4dc;
}
.dq-signatory-footer {
  border-top: 1px dashed #d6dbe3;
}
.signatory-pad {
  width: 100%;
  max-width: 520px;
  height: 160px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  touch-action: none;
  cursor: crosshair;
}
</style>
